/**
 * useFilterBuilder Composable
 *
 * Manages a recursive FilterGroup tree for the advanced AND/OR filter builder.
 * Provides tree manipulation, validation, and clean export.
 */

import { ref, computed, type Ref } from 'vue'
import type { FilterCondition, FilterGroup } from '../api/types'
import { isFilterCondition, isFilterGroup } from '../api/types'

/** Maximum nesting depth for filter groups */
const MAX_DEPTH = 5

/** Maximum total conditions across the entire tree */
const MAX_CONDITIONS = 50

/** Path to a node in the tree — array of indices */
export type NodePath = number[]

/** Creates an empty filter condition */
export function createEmptyCondition(): FilterCondition {
    return { field: '', op: '$eq', value: '' }
}

/** Creates an empty filter group */
export function createEmptyGroup(operator: 'AND' | 'OR' = 'AND'): FilterGroup {
    return { operator, conditions: [] }
}

/**
 * Composable for managing a recursive filter tree.
 *
 * @param initialTree - Optional initial filter tree. Defaults to empty AND group.
 */
export function useFilterBuilder(initialTree?: FilterGroup) {
    const filterTree: Ref<FilterGroup> = ref(
        initialTree
            ? JSON.parse(JSON.stringify(initialTree))
            : createEmptyGroup()
    )

    // ─── Tree Traversal ─────────────────────────────────────────

    /**
     * Get a reference to a group at a given path.
     * Returns null if the path is invalid or doesn't point to a group.
     */
    function getGroupAtPath(path: NodePath): FilterGroup | null {
        if (path.length === 0) return filterTree.value

        let current: FilterCondition | FilterGroup = filterTree.value
        for (const index of path) {
            if (!isFilterGroup(current)) return null
            if (index < 0 || index >= current.conditions.length) return null
            current = current.conditions[index]
        }

        return isFilterGroup(current) ? current : null
    }

    /**
     * Get the parent group and child index for a given path.
     * Returns null if the path is invalid.
     */
    function getParentAndIndex(path: NodePath): { parent: FilterGroup; index: number } | null {
        if (path.length === 0) return null

        const parentPath = path.slice(0, -1)
        const childIndex = path[path.length - 1]
        const parent = getGroupAtPath(parentPath)

        if (!parent || childIndex < 0 || childIndex >= parent.conditions.length) return null

        return { parent, index: childIndex }
    }

    // ─── Mutations ──────────────────────────────────────────────

    /**
     * Add a condition to the group at the given path.
     * Path defaults to root group.
     */
    function addCondition(parentPath: NodePath = []): boolean {
        if (conditionCount.value >= MAX_CONDITIONS) return false

        const group = getGroupAtPath(parentPath)
        if (!group) return false

        group.conditions.push(createEmptyCondition())
        return true
    }

    /**
     * Add a nested group to the group at the given path.
     * Respects MAX_DEPTH limit.
     */
    function addGroup(parentPath: NodePath = [], operator: 'AND' | 'OR' = 'OR'): boolean {
        if (getDepthAtPath(parentPath) >= MAX_DEPTH) return false
        if (conditionCount.value >= MAX_CONDITIONS) return false

        const group = getGroupAtPath(parentPath)
        if (!group) return false

        const newGroup = createEmptyGroup(operator)
        newGroup.conditions.push(createEmptyCondition())
        group.conditions.push(newGroup)
        return true
    }

    /**
     * Remove a node (condition or group) at the given path.
     */
    function removeNode(path: NodePath): boolean {
        const result = getParentAndIndex(path)
        if (!result) return false

        result.parent.conditions.splice(result.index, 1)
        return true
    }

    /**
     * Update a condition at the given path.
     */
    function updateCondition(path: NodePath, updates: Partial<FilterCondition>): boolean {
        const result = getParentAndIndex(path)
        if (!result) return false

        const node = result.parent.conditions[result.index]
        if (!isFilterCondition(node)) return false

        Object.assign(node, updates)
        return true
    }

    /**
     * Toggle the operator of a group at the given path.
     */
    function toggleGroupOperator(path: NodePath = []): boolean {
        const group = getGroupAtPath(path)
        if (!group) return false

        group.operator = group.operator === 'AND' ? 'OR' : 'AND'
        return true
    }

    /**
     * Reset the tree to an empty state.
     */
    function reset(): void {
        filterTree.value = createEmptyGroup()
    }

    /**
     * Replace the entire tree with a new one.
     */
    function setTree(tree: FilterGroup): void {
        filterTree.value = JSON.parse(JSON.stringify(tree))
    }

    // ─── Depth Calculation ──────────────────────────────────────

    function getDepthAtPath(path: NodePath): number {
        let depth = 0
        for (const index of path) {
            const group = getGroupAtPath(path.slice(0, depth))
            if (group && index < group.conditions.length && isFilterGroup(group.conditions[index])) {
                depth++
            }
        }
        return depth + 1 // +1 for root
    }

    function getMaxDepth(group: FilterGroup, currentDepth = 1): number {
        let maxDepth = currentDepth
        for (const child of group.conditions) {
            if (isFilterGroup(child)) {
                maxDepth = Math.max(maxDepth, getMaxDepth(child, currentDepth + 1))
            }
        }
        return maxDepth
    }

    // ─── Computed Properties ────────────────────────────────────

    /** Total number of conditions (leaf nodes) in the tree */
    const conditionCount = computed((): number => {
        function count(group: FilterGroup): number {
            return group.conditions.reduce((sum, child) => {
                if (isFilterCondition(child)) return sum + 1
                if (isFilterGroup(child)) return sum + count(child)
                return sum
            }, 0)
        }
        return count(filterTree.value)
    })

    /** Whether the current tree exceeds the maximum depth */
    const exceedsMaxDepth = computed((): boolean => {
        return getMaxDepth(filterTree.value) > MAX_DEPTH
    })

    /** Whether the current tree exceeds the maximum conditions */
    const exceedsMaxConditions = computed((): boolean => {
        return conditionCount.value > MAX_CONDITIONS
    })

    /** Whether the filter tree is valid for saving/applying */
    const isValid = computed((): boolean => {
        if (conditionCount.value === 0) return false
        if (exceedsMaxDepth.value) return false
        if (exceedsMaxConditions.value) return false

        function hasValidCondition(group: FilterGroup): boolean {
            return group.conditions.some((child) => {
                if (isFilterCondition(child)) {
                    return child.field !== '' && child.value !== '' && child.value !== null && child.value !== undefined
                }
                if (isFilterGroup(child)) {
                    return hasValidCondition(child)
                }
                return false
            })
        }

        return hasValidCondition(filterTree.value)
    })

    /**
     * Export the tree with empty/invalid conditions stripped.
     * Returns null if no valid conditions remain.
     */
    function toCleanTree(): FilterGroup | null {
        function cleanGroup(group: FilterGroup): FilterGroup | null {
            const validConditions: (FilterCondition | FilterGroup)[] = []

            for (const child of group.conditions) {
                if (isFilterCondition(child)) {
                    if (child.field && child.value !== '' && child.value !== null && child.value !== undefined) {
                        validConditions.push({ ...child })
                    }
                } else if (isFilterGroup(child)) {
                    const cleaned = cleanGroup(child)
                    if (cleaned && cleaned.conditions.length > 0) {
                        validConditions.push(cleaned)
                    }
                }
            }

            if (validConditions.length === 0) return null

            return {
                operator: group.operator,
                conditions: validConditions,
            }
        }

        return cleanGroup(filterTree.value)
    }

    return {
        filterTree,
        addCondition,
        addGroup,
        removeNode,
        updateCondition,
        toggleGroupOperator,
        reset,
        setTree,
        conditionCount,
        exceedsMaxDepth,
        exceedsMaxConditions,
        isValid,
        toCleanTree,
    }
}
